import { useEffect, useRef } from "react";

/**
 * 右から出るオーバーレイ。
 *
 * JSON の編集は行数が多く、画面に埋め込むと縦が足りない。
 * 広い面で編集できるようにする。
 *
 * ★ 奥行きは Float（影 + border）。基盤の Elevation に合わせる。
 * ★ Esc と背景クリックで閉じられること。閉じ方が 1 つしか無いと、
 * 「戻れない」画面になる。
 */

export interface DrawerProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
  /** 下部に並べる操作（保存など）。 */
  readonly footer?: React.ReactNode;
}

export function Drawer({
  open,
  title,
  onClose,
  children,
  footer,
}: DrawerProps): React.JSX.Element | null {
  const panel = useRef<HTMLDivElement | null>(null);

  // Esc で閉じる。開いている間だけ拾う。
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    globalThis.addEventListener?.("keydown", onKey);
    return () => globalThis.removeEventListener?.("keydown", onKey);
  }, [open, onClose]);

  // 開いたら中へ視点を移す。背後を触らせない。
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="drawer-scrim" onMouseDown={onClose} data-testid="drawer-scrim">
      <div
        ref={panel}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        // 中のクリックで閉じない。
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="drawer-head">
          <h3>{title}</h3>
          <span className="spacer" />
          <button type="button" onClick={onClose} aria-label="閉じる">
            閉じる
          </button>
        </header>

        <div className="drawer-body">{children}</div>

        {footer !== undefined && <footer className="drawer-foot">{footer}</footer>}
      </div>
    </div>
  );
}
